import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { createPrescription, getPrescriptionByAppointmentId, updatePrescription, sendPrescriptionFormToDoctor } from '../../services/prescriptionApi';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Loader2, AlertCircle, Pill, Search, Calendar, FileText, CheckCircle, Upload, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const PrescriptionCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const appointmentIdFromUrl = searchParams.get('appointmentId');
  const [loading, setLoading] = useState(false);
  const [fetchingAppointment, setFetchingAppointment] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [lead, setLead] = useState(null);
  const [appointmentIdInput, setAppointmentIdInput] = useState(appointmentIdFromUrl || '');
  const [appointmentLookupError, setAppointmentLookupError] = useState('');
  const [formData, setFormData] = useState({
    appointmentId: appointmentIdFromUrl || '',
    chiefComplaints: '',
    pastMedicalHistory: '',
    comorbidities: ''
  });
  const [errors, setErrors] = useState({});
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [lastSaved, setLastSaved] = useState(null);
  const [existingPrescriptionId, setExistingPrescriptionId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [backendAutoSaving, setBackendAutoSaving] = useState(false);

  const getDraftKey = useCallback(() => {
    const id = formData.appointmentId || appointmentIdFromUrl || 'new';
    return `prescription_draft_${id}`;
  }, [formData.appointmentId, appointmentIdFromUrl]);

  const saveDraft = useCallback(async (draftData) => {
    try {
      const key = getDraftKey();
      const payload = {
        ...draftData,
        savedAt: new Date().toISOString(),
        appointmentId: draftData.appointmentId || appointmentIdFromUrl || ''
      };
      localStorage.setItem(key, JSON.stringify(payload));
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
    }
  }, [getDraftKey, appointmentIdFromUrl]);

  const loadDraft = useCallback(() => {
    try {
      const key = getDraftKey();
      const draft = localStorage.getItem(key);
      if (draft) {
        const parsed = JSON.parse(draft);
        setFormData({
          appointmentId: parsed.appointmentId || appointmentIdFromUrl || '',
          chiefComplaints: parsed.chiefComplaints || '',
          pastMedicalHistory: parsed.pastMedicalHistory || '',
          comorbidities: parsed.comorbidities || ''
        });
        if (parsed.appointmentId && !appointmentIdFromUrl) {
          setAppointmentIdInput(parsed.appointmentId);
          fetchAppointment(parsed.appointmentId);
        }
        setLastSaved(new Date(parsed.savedAt));
        setAutoSaveStatus('restored');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
  }, [getDraftKey, appointmentIdFromUrl]);

  const checkExistingPrescription = useCallback(async (apptId) => {
    if (!apptId) return;
    try {
      const response = await getPrescriptionByAppointmentId(apptId);
      const prescription = response?.data;
      if (prescription) {
        setExistingPrescriptionId(prescription._id);
        setIsEditing(true);
        setEmailSent(Boolean(prescription.prescriptionFormSentAt));
        setFormData({
          appointmentId: prescription.appointmentId?._id || prescription.appointmentId || apptId,
          chiefComplaints: prescription.chiefComplaints || '',
          pastMedicalHistory: prescription.pastMedicalHistory || '',
          comorbidities: prescription.leadId?.comorbidities || prescription.comorbidities || ''
        });
        setAutoSaveStatus('restored');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('No existing prescription found or error fetching:', error.message);
    }
  }, [appointmentIdFromUrl]);

  const autoSaveToBackend = useCallback(async () => {
    if (!existingPrescriptionId) return;
    setBackendAutoSaving(true);
    try {
      const payload = { ...formData };
      await updatePrescription(existingPrescriptionId, payload);
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Backend auto-save failed:', error);
    } finally {
      setBackendAutoSaving(false);
    }
  }, [existingPrescriptionId, formData]);

  useEffect(() => {
    loadDraft();
  }, []);

  useEffect(() => {
    if (!formData.appointmentId && !appointmentIdFromUrl) return;
    const timeoutId = setTimeout(() => {
      saveDraft(formData);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, saveDraft, appointmentIdFromUrl]);

  useEffect(() => {
    if (!existingPrescriptionId) return;
    const intervalId = setInterval(() => {
      autoSaveToBackend();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [existingPrescriptionId, autoSaveToBackend]);

  useEffect(() => {
    if (appointmentIdFromUrl) {
      setAppointmentIdInput(appointmentIdFromUrl);
      setFormData((prev) => ({ ...prev, appointmentId: appointmentIdFromUrl }));
      fetchAppointment(appointmentIdFromUrl);
    }
  }, [appointmentIdFromUrl]);

  const loadAppointment = async (e) => {
    e?.preventDefault?.();
    const id = appointmentIdInput.trim();
    if (!id) return;
    setAppointmentLookupError('');
    setFetchingAppointment(true);
    try {
      const response = await api.get(`/appointments/${id}`);
      const data = response.data?.data || null;
      setAppointment(data);
      setFormData((prev) => ({ ...prev, appointmentId: id }));
      if (!data) setAppointmentLookupError('Appointment not found');
      if (data?.leadId?._id) {
        setLead(data.leadId);
        setFormData((prev) => ({
          ...prev,
          chiefComplaints: data.leadId.chiefComplaints || data.leadId.clinicalIntake?.medicalDetails?.history?.currentIllness || prev.chiefComplaints,
          pastMedicalHistory: data.leadId.pastMedicalHistory || data.leadId.clinicalIntake?.medicalDetails?.history?.pastDiseases || prev.pastMedicalHistory,
          comorbidities: data.leadId.comorbidities || prev.comorbidities || ''
        }));
      }
      await checkExistingPrescription(id);
    } catch (error) {
      setAppointment(null);
      setLead(null);
      setAppointmentLookupError(error.response?.data?.message || 'Failed to load appointment');
    } finally {
      setFetchingAppointment(false);
    }
  };

  const fetchAppointment = async (id) => {
    setFetchingAppointment(true);
    try {
      const response = await api.get(`/appointments/${id}`);
      const data = response.data?.data || null;
      setAppointment(data);
      if (data?.leadId?._id) {
        setLead(data.leadId);
        setFormData((prev) => ({
          ...prev,
          chiefComplaints: data.leadId.chiefComplaints || data.leadId.clinicalIntake?.medicalDetails?.history?.currentIllness || prev.chiefComplaints,
          pastMedicalHistory: data.leadId.pastMedicalHistory || data.leadId.clinicalIntake?.medicalDetails?.history?.pastDiseases || prev.pastMedicalHistory,
          comorbidities: data.leadId.comorbidities || prev.comorbidities || ''
        }));
      }
      await checkExistingPrescription(id);
    } catch (error) {
      toast.error('Failed to load appointment');
    } finally {
      setFetchingAppointment(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.chiefComplaints?.trim()) {
      newErrors.chiefComplaints = 'Chief complaints is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const savePrescription = useCallback(async (shouldNavigate = true) => {
    if (!validate()) return false;

    setLoading(true);
    try {
      const payload = { ...formData };
      if (existingPrescriptionId) {
        await updatePrescription(existingPrescriptionId, payload);
        toast.success('Prescription updated successfully');
      } else {
        const prescription = await createPrescription(payload);
        setExistingPrescriptionId(prescription.data?._id || null);
        toast.success('Prescription created successfully');
      }

      if (!emailSent && existingPrescriptionId) {
        try {
          await sendPrescriptionFormToDoctor(existingPrescriptionId);
          setEmailSent(true);
          toast.success('Prescription form sent to doctor');
        } catch (emailError) {
          console.error('Failed to send prescription form to doctor:', emailError);
          toast.error('Prescription saved but failed to send email to doctor');
        }
      }

      const key = getDraftKey();
      localStorage.removeItem(key);
      setLastSaved(new Date());

      if (shouldNavigate) {
        if (appointmentIdFromUrl) {
          navigate(`/appointments/${appointmentIdFromUrl}`);
        } else {
          navigate('/prescriptions');
        }
      }

      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save prescription');
      return false;
    } finally {
      setLoading(false);
    }
  }, [formData, existingPrescriptionId, emailSent, appointmentIdFromUrl, navigate, validate, getDraftKey]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await savePrescription(true);
  };

  const leadDocuments = lead?.documents || [];
  const idProofs = leadDocuments.filter(d => d.type === 'id_proof');
  const medicalReports = leadDocuments.filter(d => d.type === 'medical_report' || !d.type);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <button
          onClick={() => navigate('/prescriptions')}
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#143E78] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Prescriptions
        </button>

        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          <div className="px-6 sm:px-8 py-6 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-100">
                <Pill className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-900 tracking-tight">{isEditing ? 'Edit Prescription' : 'Create Prescription'}</h1>
                <p className="text-sm font-bold text-slate-500 mt-0.5">{isEditing ? 'Update prescription details' : 'Fill out the prescription form for the patient'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {autoSaveStatus === 'saved' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-emerald-100">
                  <CheckCircle className="w-3.5 h-3.5" /> {backendAutoSaving ? 'Saving...' : 'Draft saved'}
                </span>
              )}
              {autoSaveStatus === 'restored' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-blue-100">
                  <Upload className="w-3.5 h-3.5" /> Draft restored
                </span>
              )}
              {autoSaveStatus === 'error' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-rose-100">
                  <AlertCircle className="w-3.5 h-3.5" /> Auto-save failed
                </span>
              )}
              {emailSent && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 text-violet-700 rounded-lg text-[10px] font-black uppercase tracking-wider border border-violet-100">
                  <CheckCircle className="w-3.5 h-3.5" /> Form sent to doctor
                </span>
              )}
              {lastSaved && autoSaveStatus === 'idle' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-500 rounded-lg text-[10px] font-black uppercase tracking-wider border border-slate-100">
                  <Clock className="w-3.5 h-3.5" /> Last saved: {lastSaved.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {fetchingAppointment && (
            <div className="px-6 sm:px-8 py-6 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#143E78]" />
              <span className="ml-2 text-sm font-bold text-slate-500">Loading appointment...</span>
            </div>
          )}

          {!fetchingAppointment && (
            <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
              {!appointment && (
                <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-5">
                  <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-3">Link Appointment</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={appointmentIdInput}
                      onChange={(e) => setAppointmentIdInput(e.target.value)}
                      placeholder="Enter Appointment ID"
                      className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                    />
                    <button
                      type="button"
                      onClick={loadAppointment}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20"
                    >
                      <Search className="w-3.5 h-3.5" /> Load
                    </button>
                  </div>
                  {appointmentLookupError && (
                    <div className="flex items-center gap-1.5 mt-3">
                      <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
                      <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{appointmentLookupError}</p>
                    </div>
                  )}
                </div>
              )}

              {appointment && (
                <div className="bg-emerald-50 border-2 border-emerald-100 rounded-2xl p-4 flex flex-wrap items-center gap-3">
                  <Calendar className="w-4 h-4 text-emerald-700" />
                  <div>
                    <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Linked Appointment</p>
                     <p className="text-xs font-black text-emerald-900">
                       {appointment.patientName}
                     </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAppointment(null); setLead(null); setAppointmentIdInput(''); setFormData((prev) => ({ ...prev, appointmentId: '' })); }}
                    className="ml-auto text-rose-600 hover:text-rose-700 text-[10px] font-black uppercase tracking-wider"
                  >
                    Change
                  </button>
                </div>
              )}

              {lead && (
                <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-4 space-y-3">
                  <p className="text-[10px] font-black text-blue-700 uppercase tracking-widest flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5" /> Linked Lead Clinical Data
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Patient Name</p>
                      <p className="text-xs font-black text-slate-900">{lead.patientName}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone</p>
                      <p className="text-xs font-black text-slate-900">{lead.patientPhone}</p>
                    </div>
                  </div>
                  {(idProofs.length > 0 || medicalReports.length > 0) && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Attached Documents</p>
                      <div className="flex flex-wrap gap-2">
                        {idProofs.map((doc, idx) => (
                          <a key={`id-${idx}`} href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-blue-200 transition-colors">
                            <FileText className="w-3 h-3" /> ID Proof {idx + 1}
                          </a>
                        ))}
                        {medicalReports.map((doc, idx) => (
                          <a key={`med-${idx}`} href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-emerald-200 transition-colors">
                            <FileText className="w-3 h-3" /> Report {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Chief Complaints <span className="text-rose-500">*</span></label>
                <textarea
                  value={formData.chiefComplaints}
                  onChange={(e) => setFormData((prev) => ({ ...prev, chiefComplaints: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Fever since 2 days, Cold and sore throat, Body ache"
                />
                {errors.chiefComplaints && (
                  <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{errors.chiefComplaints}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Past Medical History</label>
                <textarea
                  value={formData.pastMedicalHistory}
                  onChange={(e) => setFormData((prev) => ({ ...prev, pastMedicalHistory: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Diabetes, Hypertension, Previous surgeries, Allergies"
                 />
               </div>

               <div className="space-y-3">
                 <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Comorbidities</label>
                 <textarea
                   value={formData.comorbidities || ''}
                   onChange={(e) => setFormData((prev) => ({ ...prev, comorbidities: e.target.value }))}
                   rows={3}
                   className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                   placeholder="e.g. Asthma, BP, Diabetes"
                 />
               </div>

               <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => navigate('/prescriptions')}
                  className="px-5 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isEditing ? 'Update Prescription' : 'Save Prescription'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionCreate;
